import React, { useMemo } from 'react';
import { useSnackbar } from 'notistack';

// Data
import { buildCreateAssessmentSessions } from '../../../lib/services/assessment-session.service';

// Components
import { ImportSessionsContainer } from './import-sessions.container';
import { getAuthUser } from '../../../lib/services/auth.service';
import { buildCreateAgeGroup, getAgeGroups } from '../../../lib/services/age-group.service';
import { getActiveCompanyId, getCompany } from '../../../lib/services/company.service';
import { getActiveAssessmentId, getAssessment } from '../../../lib/services/assessment.service';
import { CASCADING_SETTINGS } from '../../../lib/types/cascading-settings.types';
import { resolveCascadingSetting } from '../../../lib/helpers/cascading-settings.helpers';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { MessageLines } from '../../_core/_ui/typography.component';
import { useSnackbarDismissAction } from '../../../lib/hooks/use-snackbar-dismiss-action';
import { useOperationErrorHandler } from '../../../lib/hooks/use-operation-error-handler';
import { getImportSessionsMessages } from './import-sessions.messages';
import { IImportSubmissionSummary } from '../../_core/Importer/importer.interface';
import { OUTCOME_SNACKBAR_MS } from '../../../lib/snackbar.constants';
import { pluralize } from '../../../lib/helpers/text.helpers';
import {
  describeInaccessibleAgeGroups,
  readInaccessibleAgeGroupIds
} from '../../../lib/helpers/age-group.helpers';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { SportTerminology } from '../../../lib/types';
import { ROLES } from '../../../../_configuration';

interface IImportSessionsGraphQL {
  onComplete?: Function;
  setSubmitError?: Function;
}

export const ImportSessionsGraphQL: React.FC<IImportSessionsGraphQL> = ({ onComplete, setSubmitError }) => {
  const user = getAuthUser();
  const { ageGroups } = getAgeGroups({requiredRoles: [ROLES.CONFIGURATION]});
  const { company } = getCompany(getActiveCompanyId());
  const { assessment } = getAssessment(getActiveAssessmentId());
  const termLookup = useCompanySportTerminology();
  const messages = useMemo(() => getImportSessionsMessages(termLookup), [termLookup]);
  const sessionTerm = termLookup[SportTerminology.SESSION].toLowerCase();
  const { enqueueSnackbar } = useSnackbar();
  const dismissAction = useSnackbarDismissAction();

  const handleImportError = useOperationErrorHandler(messages.errImportFailed);

  const { createAssessmentSessions } = buildCreateAssessmentSessions();
  const { createAgeGroup } = buildCreateAgeGroup();

  // Imported ice times carry no RSVP column and get their roster manually, so nothing
  // downstream sets the flag — the default is stamped here, at create time.
  const isAttendanceRequested = resolveCascadingSetting(CASCADING_SETTINGS.IS_ATTENDANCE_REQUESTED, { assessment, company });

  // One line per thing that actually happened — the age groups the file introduced are
  // reported alongside the ice times, never folded into a bare "imported".
  const describeOutcomes = (created: number, ageGroupsCreated: number) => {
    const lines: React.ReactNode[] = [];

    if (created) lines.push(<div key="created"><IntlMsg {...messages.snackCreated}
      values={{ count: created, noun: pluralize(created, sessionTerm) }} /></div>);

    if (ageGroupsCreated) lines.push(<div key="ageGroups"><IntlMsg {...messages.snackAgeGroupsCreated}
      values={{ count: ageGroupsCreated, noun: pluralize(ageGroupsCreated, 'group') }} /></div>);

    return lines;
  };

  // The create refuses the whole file unless every age group in it is configurable, and names
  // the ones it rejected — so the failure says which groups to fix rather than "try again".
  const handleCreateError = (error: unknown) => {
    const inaccessibleIds = readInaccessibleAgeGroupIds(error);
    if (!inaccessibleIds.length) return handleImportError('createAssessmentSessions', error);

    const names = describeInaccessibleAgeGroups(inaccessibleIds,
      (id) => ageGroups.find((ageGroup: IAgeGroup) => ageGroup._id === id)?.name);

    return handleImportError('createAssessmentSessions', error, {
      message: messages.errInaccessibleAgeGroups,
      values: {
        names,
        verb: pluralize(inaccessibleIds.length, 'is', 'are'),
        noun: pluralize(inaccessibleIds.length, 'that age group', 'those age groups')
      }
    });
  };

  const handleCreate = (rows: IAssessmentSession[], summary: IImportSubmissionSummary, ageGroupsCreated: number) => {
    if (setSubmitError) setSubmitError(null);
    const data = rows.map(row => ({ ...row, assessmentId: user?.settings.activeAssessmentId, isAttendanceRequested }));
    // Rows the review step withheld never reached the server, so they are only ever counted here.
    const skippedCount = summary.skippedRowCount;

    if (onComplete) onComplete();

    return createAssessmentSessions(data).then((result) => {
      const created = result.data?.createAssessmentSessions?.length || 0;
      const outcomeLines = describeOutcomes(created, ageGroupsCreated);

      if (outcomeLines.length) enqueueSnackbar(<MessageLines>{outcomeLines}</MessageLines>,
        { variant: 'success', autoHideDuration: OUTCOME_SNACKBAR_MS, action: dismissAction });

      if (skippedCount && setSubmitError) setSubmitError(<IntlMsg {...messages.errSkipped}
        values={{ count: skippedCount, noun: pluralize(skippedCount, 'row') }} />);
    }).catch(handleCreateError);
  }

  return <ImportSessionsContainer
    createAssessmentSessions={handleCreate}
    createAgeGroup={createAgeGroup}
    onImportError={handleImportError}
    ageGroups={ageGroups}
  />;
};
